// ============================================================
// SmartLearnIOTProjectOverview.jsx
//
// Course content page: architectural overview of the "Smart IoT
// Academy" full-stack capstone project.
//
// Fixes applied:
//   1) the ESP32 publisher snippet (`device/esp32_pub.cpp`) called
//      `reconnect()` without ever defining it, referenced an
//      undeclared `client` and `dht`, and had no `setup()` at all —
//      the ESP32 Arduino core requires both setup() and loop() to
//      link. Rewrote it as a complete, compiling sketch: WiFi
//      connect-wait loop, PubSubClient + DHT declared and
//      initialized, a real `reconnect()`, and a NaN check on the
//      sensor read.
//   2) the Node.js listener called `saveToDatabase(...)`, a function
//      the snippet never defines — left as the integration point it
//      is meant to be, but labeled with a comment pointing at the
//      "Data Persistence" card below so it doesn't read as a typo.
//   3) the copy button fired `navigator.clipboard.writeText()`
//      without awaiting it or catching a failure — it showed the
//      "copied" checkmark even if the copy silently failed. Wrapped
//      in async/await + try/catch.
//   4) removed two dead imports (FaDatabase, MdDevicesOther — never
//      rendered).
//   5) re-themed from this lesson's original one-off slate/indigo
//      palette to the app-wide dark-purple/goldenrod look, moved to
//      arrow-function components, and replaced every react-icons /
//      brand-logo icon (React, Node.js, MQTT, C++, Vercel, MongoDB)
//      with semantic Material Icons.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import WebIcon from "@mui/icons-material/Web";
import DnsIcon from "@mui/icons-material/Dns";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import MemoryIcon from "@mui/icons-material/Memory";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TerminalIcon from "@mui/icons-material/Terminal";
import CloudIcon from "@mui/icons-material/Cloud";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StorageIcon from "@mui/icons-material/Storage";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const STACK = [
    { title: "Frontend", icon: <WebIcon />, color: BRAND.gold, tech: "React 18, MUI v5, Chart.js, React Router." },
    { title: "Backend", icon: <DnsIcon />, color: BRAND.teal, tech: "Node.js, Express, MQTT.js, JWT Auth." },
    { title: "Connectivity", icon: <SettingsInputAntennaIcon />, color: BRAND.gold, tech: "HiveMQ Broker, WebSockets, REST APIs." },
    { title: "Device", icon: <MemoryIcon />, color: BRAND.teal, tech: "ESP32 (C++), Raspberry Pi (Python), MicroPython." },
];

const INFRASTRUCTURE = [
    { title: "Frontend Deployment", icon: <CloudIcon />, desc: "Hosted on Vercel with CI/CD integration for rapid UI updates." },
    { title: "Backend Hosting", icon: <CloudUploadIcon />, desc: "Node.js service running on Railway or AWS EC2 instances." },
    { title: "Data Persistence", icon: <StorageIcon />, desc: "MongoDB Atlas for storing time-series sensor events and user logs." },
    { title: "Real-time Mesh", icon: <DeviceHubIcon />, desc: "HiveMQ public/private brokers for global message distribution." },
];

const DIRECTORY_TREE = `iot-academy/
├── client/              # React Application (Vite/CRA)
│   ├── src/pages/       # Educational Modules
├── server/              # Node.js API & MQTT Service
│   ├── mqttListener.js  # Real-time data handler
├── device/              # Firmware & Edge Scripts
│   ├── esp32_pub.cpp    # C++ Hardware Logic
│   └── sensor.py        # Python Gateway Script
└── docker-compose.yml   # Container Orchestration`;

const MQTT_LISTENER = `const mqtt = require("mqtt");
const client = mqtt.connect("mqtt://broker.hivemq.com");

client.on("connect", () => {
  client.subscribe("iot/temperature");
});

client.on("message", (topic, msg) => {
  // Plug in your persistence layer here — e.g. a MongoDB Atlas
  // insert into the time-series collection (see "Data Persistence"
  // under Infrastructure & Scale below).
  saveToDatabase(topic, msg.toString());
});`;

// A complete, compiling sketch — the original called an undefined
// reconnect(), referenced undeclared client/dht, and had no setup()
// at all (Arduino requires both setup() and loop() to link).
const ESP32_PUBLISHER = `#include <WiFi.h>
#include <PubSubClient.h>
#include <DHT.h>

#define DHTPIN 4
#define DHTTYPE DHT22

const char* ssid = "YOUR_WIFI_SSID";
const char* password = "YOUR_WIFI_PASSWORD";
const char* mqttServer = "broker.hivemq.com";

WiFiClient espClient;
PubSubClient client(espClient);
DHT dht(DHTPIN, DHTTYPE);

void reconnect() {
  while (!client.connected()) {
    client.connect("ESP32Publisher"); // retry until the broker accepts us
    if (!client.connected()) delay(2000);
  }
}

void setup() {
  Serial.begin(115200);
  dht.begin();

  WiFi.begin(ssid, password);
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }

  client.setServer(mqttServer, 1883);
}

void loop() {
  if (!client.connected()) reconnect();
  client.loop();

  float t = dht.readTemperature();
  if (!isnan(t)) {
    client.publish("iot/temperature", String(t).c_str());
  }

  delay(5000); // 5-second interval
}`;

// Sub-components (arrow-function style, defined before first use)

const StackCard = ({ icon, title, tech, color }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: color, transform: "translateY(-4px)" },
        }}
    >
        <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}26`, color }}>{icon}</Avatar>
                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>{tech}</Typography>
        </Stack>
    </Paper>
);

const CodeTerminal = ({ code, label, color = BRAND.gold }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 12, color: "#FCD34D", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const SmartLearnIOTProjectOverview = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Project Blueprint · Capstone"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Smart IoT <span style={{ color: BRAND.teal }}>Project Overview</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    A comprehensive, full-stack educational ecosystem. From silicon to screen, this platform
                    integrates real-time data streams, secure cloud logic, and intuitive React dashboards.
                </Typography>
            </Box>

            {/* 1. PROJECT STACK */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <AccountTreeIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>The Technology Stack</Typography>
                </Box>
                <Grid container spacing={3}>
                    {STACK.map((item) => (
                        <Grid item xs={12} sm={6} md={3} key={item.title}>
                            <StackCard {...item} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* 2. DIRECTORY STRUCTURE */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <AccountTreeIcon sx={{ fontSize: 24, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Repository Architecture</Typography>
                </Box>
                <CodeTerminal label="ROOT_DIRECTORY" code={DIRECTORY_TREE} />
            </Box>

            <Divider sx={{ borderColor: BRAND.border, mb: 10 }} />

            {/* 3. LOGIC IMPLEMENTATION */}
            <Box sx={{ mb: 10 }}>
                <Grid container spacing={6}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <DnsIcon sx={{ color: BRAND.gold }} />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Backend MQTT Listener</Typography>
                        </Box>
                        <CodeTerminal label="server/mqttListener.js" code={MQTT_LISTENER} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <MemoryIcon sx={{ color: BRAND.teal }} />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>ESP32 Hardware Publisher</Typography>
                        </Box>
                        <CodeTerminal color={BRAND.teal} label="device/esp32_pub.cpp" code={ESP32_PUBLISHER} />
                    </Grid>
                </Grid>
            </Box>

            {/* 4. DEPLOYMENT & DATABASE */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4 }}>Infrastructure & Scale</Typography>
                <Grid container spacing={3}>
                    {INFRASTRUCTURE.map((item) => (
                        <Grid item xs={12} md={6} key={item.title}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}`, borderRadius: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold }}>{item.icon}</Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 700 }}>{item.title}</Typography>
                                        <Typography variant="caption" sx={{ color: BRAND.muted }}>{item.desc}</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>System Architecture Validated?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Configure your .env files and launch the Smart IoT Academy locally.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Clone Repository" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Full API Documentation" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default SmartLearnIOTProjectOverview;
